/**
 * ApplicationActions — the per-application admin controls, ported from the
 * legacy console (pages/AdminApplicationsPage.js) onto the new surface.
 *
 * Covers approve, reject, unapprove, verifier levels, reminder and the
 * blanket document waiver. Per-requirement waivers and per-document decisions
 * live inline with their rows on the detail page instead.
 *
 * Validation mirrors the server exactly: a rejection reason is 3-1000 chars,
 * verifier levels must be a non-empty subset of client/contractor. Approval is
 * additionally gated server-side on the checklist being complete, so that
 * error is surfaced verbatim rather than pre-empted.
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';
import {
  CheckCircleOutline,
  Block,
  Undo,
  NotificationsActive,
  Tune,
  RuleFolder,
} from '@mui/icons-material';

import adminApplicationsService from '../../services/adminApplicationsService';
import { status as statusTokens, text } from '../../theme/colors';

// Stored values stay client/contractor; only the labels differ.
const VERIFIER_TYPES = [
  { value: 'client', label: 'Work' },
  { value: 'contractor', label: 'Contractor' },
];

const VERIFIER_ROLES = ['verifier', 'professional_verifier', 'quality_inspector'];

const REASON_MIN = 3;
const REASON_MAX = 1000;

const ApplicationActions = ({ application, onChanged }) => {
  const [dialog, setDialog] = useState(null);
  const [reason, setReason] = useState('');
  const [levels, setLevels] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const id = application?.id;
  const isVerifier = VERIFIER_ROLES.includes(application?.role);
  const approval = application?.approval_status;
  const waived = !!application?.documentsWaived;

  const open = (key) => {
    setError(null);
    setNotice(null);
    setReason('');
    setLevels(application?.verifierTypes?.length ? application.verifierTypes : ['client']);
    setDialog(key);
  };

  const close = () => {
    if (busy) return;
    setDialog(null);
    setError(null);
  };

  const run = async (fn, successMessage) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fn();
      setDialog(null);
      setNotice(res?.message || successMessage);
      onChanged?.();
      // Layout listens for this to refresh its sidebar counts, so the pending
      // badge drops now instead of on the next 60s poll.
      window.dispatchEvent(new Event('eqaya:unread-refresh'));
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'That action could not be completed.');
    } finally {
      setBusy(false);
    }
  };

  const toggleLevel = (value) =>
    setLevels((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));

  const reasonInvalid = reason.trim().length < REASON_MIN || reason.trim().length > REASON_MAX;

  return (
    <>
      {notice && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {error && !dialog && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        {approval !== 'approved' && (
          <Button
            size="small"
            variant="contained"
            disableElevation
            startIcon={<CheckCircleOutline />}
            onClick={() => open(isVerifier ? 'approve-verifier' : 'approve')}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: statusTokens.success,
              '&:hover': { bgcolor: statusTokens.successDark },
            }}
          >
            Approve
          </Button>
        )}

        {approval !== 'rejected' && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<Block />}
            onClick={() => open('reject')}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              color: statusTokens.error,
              borderColor: `${statusTokens.error}66`,
              '&:hover': { borderColor: statusTokens.error },
            }}
          >
            Reject
          </Button>
        )}

        {approval === 'approved' && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<Undo />}
            onClick={() => open('unapprove')}
            sx={{ textTransform: 'none', fontWeight: 600, color: text.secondary }}
          >
            Move back to pending
          </Button>
        )}

        {isVerifier && approval === 'approved' && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<Tune />}
            onClick={() => open('levels')}
            sx={{ textTransform: 'none', fontWeight: 600, color: text.secondary }}
          >
            Verifier levels
          </Button>
        )}

        <Button
          size="small"
          variant="outlined"
          startIcon={<NotificationsActive />}
          onClick={() => open('remind')}
          sx={{ textTransform: 'none', fontWeight: 600, color: text.secondary }}
        >
          Send reminder
        </Button>

        <Button
          size="small"
          variant="outlined"
          startIcon={<RuleFolder />}
          onClick={() => open('waive')}
          sx={{ textTransform: 'none', fontWeight: 600, color: text.secondary }}
        >
          {waived ? 'Remove document waiver' : 'Waive documents'}
        </Button>
      </Box>

      {/* Approve — contractor (no levels to choose) */}
      <Dialog open={dialog === 'approve'} onClose={close} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>Approve this application?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5 }}>
            {application?.email} gains full access to the platform and is emailed the decision.
          </DialogContentText>
          {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={close} disabled={busy} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            disableElevation
            disabled={busy}
            onClick={() => run(() => adminApplicationsService.approve(id), 'Application approved')}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutline />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: statusTokens.success,
              '&:hover': { bgcolor: statusTokens.successDark },
            }}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve — verifier, with levels */}
      <Dialog open={dialog === 'approve-verifier'} onClose={close} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>Approve this verifier?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5 }}>
            Choose what {application?.email} may verify. This is stored as their verifier levels.
          </DialogContentText>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {VERIFIER_TYPES.map((t) => (
              <FormControlLabel
                key={t.value}
                control={
                  <Checkbox
                    checked={levels.includes(t.value)}
                    onChange={() => toggleLevel(t.value)}
                  />
                }
                label={`${t.label} verifications`}
              />
            ))}
          </Box>
          {levels.length === 0 && (
            <Typography variant="caption" sx={{ color: statusTokens.error }}>
              Pick at least one level.
            </Typography>
          )}
          {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={close} disabled={busy} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            disableElevation
            disabled={busy || levels.length === 0}
            onClick={() =>
              run(() => adminApplicationsService.approve(id, levels), 'Application approved')
            }
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutline />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: statusTokens.success,
              '&:hover': { bgcolor: statusTokens.successDark },
            }}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject */}
      <Dialog open={dialog === 'reject'} onClose={close} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>Reject this application?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            The applicant is emailed the reason, so write it for them to read.
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Reason (required)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            error={!!reason && reasonInvalid}
            helperText={`${REASON_MIN}-${REASON_MAX} characters.`}
          />
          {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={close} disabled={busy} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            disableElevation
            disabled={busy || reasonInvalid}
            onClick={() =>
              run(
                () => adminApplicationsService.reject(id, reason.trim()),
                'Application rejected'
              )
            }
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <Block />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: statusTokens.error,
              '&:hover': { bgcolor: statusTokens.errorDark },
            }}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unapprove */}
      <Dialog open={dialog === 'unapprove'} onClose={close} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>Move back to pending?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This reverses the approval and returns the application to the review queue.
          </DialogContentText>
          {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={close} disabled={busy} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            disableElevation
            disabled={busy}
            onClick={() =>
              run(() => adminApplicationsService.unapprove(id), 'Moved back to pending')
            }
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <Undo />}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Move to pending
          </Button>
        </DialogActions>
      </Dialog>

      {/* Verifier levels */}
      <Dialog open={dialog === 'levels'} onClose={close} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Verifier levels</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5 }}>
            What this verifier is allowed to verify.
          </DialogContentText>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {VERIFIER_TYPES.map((t) => (
              <FormControlLabel
                key={t.value}
                control={
                  <Checkbox
                    checked={levels.includes(t.value)}
                    onChange={() => toggleLevel(t.value)}
                  />
                }
                label={`${t.label} verifications`}
              />
            ))}
          </Box>
          {levels.length === 0 && (
            <Typography variant="caption" sx={{ color: statusTokens.error }}>
              At least one level is required.
            </Typography>
          )}
          {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={close} disabled={busy} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            disableElevation
            disabled={busy || levels.length === 0}
            onClick={() =>
              run(
                () => adminApplicationsService.setVerifierTypes(id, levels),
                'Verifier levels updated'
              )
            }
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <Tune />}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Save levels
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reminder */}
      <Dialog open={dialog === 'remind'} onClose={close} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>Send a reminder?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Emails {application?.email} about the outstanding parts of their application.
          </DialogContentText>
          {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={close} disabled={busy} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            disableElevation
            disabled={busy}
            onClick={() => run(() => adminApplicationsService.remind(id), 'Reminder sent')}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <NotificationsActive />}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Send reminder
          </Button>
        </DialogActions>
      </Dialog>

      {/* Blanket document waiver */}
      <Dialog open={dialog === 'waive'} onClose={close} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>
          {waived ? 'Remove the document waiver?' : 'Waive document requirements?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {waived
              ? 'Their document requirements will apply again, and the application may stop being approvable.'
              : 'Every required document is treated as satisfied for this applicant, so they can be approved without uploading them.'}
          </DialogContentText>
          {!waived && (
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Reason (optional)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              helperText="Kept on the account record."
            />
          )}
          {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={close} disabled={busy} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            disableElevation
            disabled={busy}
            onClick={() =>
              run(
                () =>
                  adminApplicationsService.waiveDocuments(
                    id,
                    !waived,
                    !waived ? reason.trim() || 'Waived by admin' : null
                  ),
                waived ? 'Waiver removed' : 'Documents waived'
              )
            }
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <RuleFolder />}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {waived ? 'Remove waiver' : 'Waive documents'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ApplicationActions;
